import React, {
  useMemo,
  useRef,
  useEffect,
  useImperativeHandle,
  useState,
  useCallback,
} from "react";
import { Slider } from "antd";
import {
  getAbsoluteZoom,
  getZoomFactor,
} from "advanced-cropper/extensions/absolute-zoom";
import CustomUploader from "./CustomUploader";
import FileUploadField from "./FileUploadField";
import { FixedCropper, ImageRestriction } from "react-advanced-cropper";
import "react-advanced-cropper/dist/style.css";

const WhiteTrashIcon = () => {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
        fill="white"
      />
    </svg>
  );
};

const ZoomOutIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <circle
      cx="10"
      cy="10"
      r="6"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
    />
    <path
      d="M14.5 14.5L20 20"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M7 10h6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const ZoomInIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <circle
      cx="10"
      cy="10"
      r="6"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
    />
    <path
      d="M14.5 14.5L20 20"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M10 7v6M7 10h6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

/** Match FixedCropper example: full visible / image size as default coordinates. */
const fixedCropperDefaultSize = ({ imageSize, visibleArea }) => ({
  width: (visibleArea || imageSize).width,
  height: (visibleArea || imageSize).height,
});

/** 16:9 stencil that fits inside the cropper boundary (responsive). */
const stencilSize169 = (state) => {
  const { width: bw, height: bh } = state.boundary;
  const aspect = 16 / 9;
  let h = bh;
  let w = h * aspect;
  if (w > bw) {
    w = bw;
    h = w / aspect;
  }
  return { width: w, height: h };
};

const ZOOM_MIN = 0;
const ZOOM_MAX = 100;

const FileUploader = ({
  limitMegabyteSize,
  fileList,
  onChange,
  showUploadList,
  enableCropper = false,
  onCrop,
  cropperRef,
  ...restProps
}) => {
  const cropperInstanceRef = useRef(null);
  /** Skip onUpdate-driven zoom sync while dragging the slider (avoids fighting the thumb). */
  const zoomSliderDraggingRef = useRef(false);
  const [zoomPercent, setZoomPercent] = useState(ZOOM_MIN);

  const uploadedFile = fileList && fileList.length > 0 ? fileList[0] : null;

  const getImageUrl = (file) => {
    if (!file) return null;
    if (typeof file === "string") return file;
    if (file.url) return file.url;
    if (file.thumbUrl) return file.thumbUrl;
    if (file.originFileObj) return URL.createObjectURL(file.originFileObj);
    return null;
  };

  const imageUrl = useMemo(() => getImageUrl(uploadedFile), [uploadedFile]);

  useEffect(() => {
    setZoomPercent(ZOOM_MIN);
  }, [imageUrl]);

  const syncZoomFromCropper = useCallback((inst) => {
    if (zoomSliderDraggingRef.current) return;
    const state = inst.getState();
    const settings = inst.getSettings();
    if (!state) return;
    const abs = getAbsoluteZoom(state, settings, true);
    setZoomPercent(Math.round(abs * 100));
  }, []);

  /**
   * Absolute zoom is 0 (zoomed out / fit) … 1 (max zoom in). Same model as the
   * official Wrapper + getZoomFactor pattern.
   */
  const applyZoomPercent = useCallback((percent, transitions) => {
    const inst = cropperInstanceRef.current;
    if (!inst || typeof inst.zoomImage !== "function") return;
    const state = inst.getState();
    const settings = inst.getSettings();
    if (!state) return;
    const absolute = Math.min(1, Math.max(0, percent / 100));
    const factor = getZoomFactor(state, settings, absolute);
    inst.zoomImage(factor, { transitions });
  }, []);

  const handleSliderChange = (value) => {
    zoomSliderDraggingRef.current = true;
    setZoomPercent(value);
    applyZoomPercent(value, false);
  };

  const handleSliderChangeComplete = () => {
    zoomSliderDraggingRef.current = false;
    const inst = cropperInstanceRef.current;
    if (inst) syncZoomFromCropper(inst);
  };

  const zoomStep = (delta) => {
    zoomSliderDraggingRef.current = false;
    const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoomPercent + delta));
    setZoomPercent(next);
    applyZoomPercent(next, true);
  };

  const handleRemove = () => {
    if (onChange) {
      onChange({ fileList: [] });
    }
  };

  const getCroppedImage = () => {
    const inst = cropperInstanceRef.current;
    if (inst && typeof inst.getCanvas === "function") {
      const canvas = inst.getCanvas();
      if (canvas) {
        return canvas.toDataURL("image/jpeg", 0.9);
      }
    }
    return null;
  };

  useImperativeHandle(
    cropperRef,
    () => ({
      getCroppedImage,
    }),
    [],
  );

  const shouldShowUploadList = uploadedFile ? false : showUploadList !== false;

  return (
    <div>
      {!uploadedFile || (uploadedFile && !enableCropper) ? (
        <CustomUploader
          {...restProps}
          fileList={fileList}
          onChange={onChange}
          showUploadList={shouldShowUploadList}
        >
          <FileUploadField
            limitMegabyteSize={limitMegabyteSize}
            uploadedFile={uploadedFile}
            onRemove={uploadedFile ? handleRemove : null}
            enableCropper={enableCropper}
            onCrop={onCrop}
          />
        </CustomUploader>
      ) : (
        <div className="file-uploader-cropper-block">
          <div style={{ width: "100%", height: "306px", position: "relative" }}>
            <FixedCropper
              ref={cropperInstanceRef}
              src={imageUrl}
              defaultSize={fixedCropperDefaultSize}
              stencilSize={stencilSize169}
              stencilProps={{
                handlers: false,
                lines: false,
                movable: false,
                resizable: false,
              }}
              imageRestriction={ImageRestriction.stencil}
              backgroundWrapperProps={{
                scaleImage: true,
                moveImage: true,
              }}
              style={{
                width: "100%",
                height: "306px",
              }}
              onReady={syncZoomFromCropper}
              onUpdate={syncZoomFromCropper}
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
              style={{
                position: "absolute",
                top: "8px",
                right: "8px",
                background: "rgba(0, 0, 0, 0.7)",
                border: "none",
                cursor: "pointer",
                padding: "6px",
                zIndex: 1,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "28px",
                height: "28px",
              }}
            >
              <WhiteTrashIcon />
            </button>
          </div>
          <div className="file-uploader-cropper-zoom">
            <button
              type="button"
              className="file-uploader-cropper-zoom__btn"
              onClick={() => zoomStep(-1)}
              disabled={zoomPercent <= ZOOM_MIN}
              aria-label="Zoom out"
            >
              <ZoomOutIcon />
            </button>
            <Slider
              className="file-uploader-cropper-zoom__slider"
              min={ZOOM_MIN}
              max={ZOOM_MAX}
              step={1}
              value={zoomPercent}
              onChange={handleSliderChange}
              onChangeComplete={handleSliderChangeComplete}
              tooltip={{ formatter: (v) => `${v}%` }}
            />
            <button
              type="button"
              className="file-uploader-cropper-zoom__btn"
              onClick={() => zoomStep(1)}
              disabled={zoomPercent >= ZOOM_MAX}
              aria-label="Zoom in"
            >
              <ZoomInIcon />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploader;
